import { isValidNumber, isValidNumericString, trim } from '@coxy/utils'
import { Big } from '@shared-core/utils/balances/big'

type TxAmountValue = {
  amount: number
  scale: number
}

type InternalAmount = string | number | undefined | null
export type AmountLike = TxAmountValue | InternalAmount

export function isValidAmount(amount: InternalAmount): amount is string | number {
  if (amount === null) return false

  if (amount === undefined) return false

  if (amount === '') return false

  if (amount === 'NaN') return false

  if (amount === 'null') return false

  if (amount === Infinity) return false

  if (amount === -Infinity) return false
  return isValidNumber(amount as string)
}

// Можно ли принять правку поля суммы. Пустое — это очистка поля, её пускаем.
// Невалидное («444a») не принимаем вовсе: поле остаётся как было, а не стирается целиком.
// isValidNumericString отсекает Infinity, 0x10, 1e5: isValidAmount их пропускает
// (проверяет через Number()), а big.js не разбирает и роняет экран
export const isAcceptableAmountInput = (amount: string): boolean =>
  amount === '' || (isValidNumericString(amount) && isValidAmount(amount))

// Есть ли значение суммы до normalizeAmount: иначе undefined/null/'' становятся «0».
export function isSetAmount(amount: AmountLike): boolean {
  return amount !== undefined && amount !== null && amount !== ''
}

export const calculateReceiveAmount = (amount: InternalAmount, exchangeRate: TxAmountValue | InternalAmount) => {
  const rate = normalizeAmount(exchangeRate)

  if (!isValidAmount(amount) || !isValidAmount(rate)) {
    return '0.00'
  }

  return Big(amount).mul(rate).toFixed(2).toString()
}

export const calculateSendAmount = (
  amount: InternalAmount,
  exchangeRate: TxAmountValue | InternalAmount,
  decimals = 4,
) => {
  const rate = normalizeAmount(exchangeRate)

  if (!isValidAmount(amount) || !isValidAmount(rate) || Big(rate).eq(0)) {
    return ''
  }

  const value = Big(amount).div(rate).toFixed(decimals)

  if (!value.includes('.')) return value

  return value.replace(/\.?0+$/, '') || '0'
}

export const parseToNumber = (input: InternalAmount): number => {
  const sanitized = String(input || '0').replace(/\s/g, '')
  const parsedNumber = parseFloat(sanitized)

  return Number.isNaN(parsedNumber) ? 0 : parsedNumber
}

export function normalizeAmount(amount: TxAmountValue | InternalAmount, defaultValue = '', isNoTransform = false) {
  const stringValue = amountFromScale(amount)
  const value = String(stringValue)?.replaceAll(/^[+-]/g, '').replaceAll(' ', '').replaceAll(',', '.') ?? defaultValue
  return isNoTransform ? value : Number(value).toString()
}

export const gtValues = (value1: AmountLike, value2: AmountLike) => {
  return Big(normalizeAmount(value1)).gt(normalizeAmount(value2))
}

export const gteValues = (value1: AmountLike, value2: AmountLike) => {
  return Big(normalizeAmount(value1)).gte(normalizeAmount(value2))
}

export const ltValues = (value1: AmountLike, value2: AmountLike) => {
  return Big(normalizeAmount(value1)).lt(normalizeAmount(value2))
}

export const eqValues = (value1: AmountLike, value2: AmountLike) => {
  return Big(normalizeAmount(value1)).eq(normalizeAmount(value2))
}

export const sumValues = (value1: AmountLike, value2: AmountLike) => {
  return Big(normalizeAmount(value1)).plus(normalizeAmount(value2)).toString()
}

export const mulValues = (value1: AmountLike, value2: AmountLike) => {
  return Big(normalizeAmount(value1)).mul(normalizeAmount(value2)).toString()
}

export const divValues = (value1: AmountLike, value2: AmountLike) => {
  try {
    return Big(normalizeAmount(value1)).div(normalizeAmount(value2)).toString()
  } catch (_) {
    return undefined
  }
}

export const subtractValues = (value1: AmountLike, value2: AmountLike) => {
  return Big(normalizeAmount(value1)).minus(normalizeAmount(value2)).toString()
}

// ROUND_UP fee до decimals — консервативная оценка для проверки «влезает ли в баланс».
export const calculatePercentFeeCeil = (amount: AmountLike, feePercent: AmountLike, decimals = 4) => {
  const normalizedAmount = normalizeAmount(amount, '')
  const normalizedPercent = normalizeAmount(feePercent, '')

  if (!isValidAmount(normalizedAmount) || !isValidAmount(normalizedPercent)) {
    return '0'
  }

  return Big(normalizedAmount).mul(normalizedPercent).div(100).toFixed(decimals, 3)
}

const stripTrailingZeros = (value: string) => (value.includes('.') ? value.replace(/\.?0+$/, '') : value)

type FitMaxAmountWithPercentFeeParams = {
  amount: AmountLike
  // Остаток, в который должны влезть amount + процентная комиссия. Это НЕ полный баланс:
  // плоские комиссии (выпуск карты и т.п.) вычитаются до вызова, иначе запас не сработает.
  available: AmountLike
  feePercent: AmountLike
  amountDecimals?: number
}

// После floor до amountDecimals уменьшаем сумму, пока amount + ceil(fee) не < available.
export const fitMaxAmountWithPercentFee = ({
  amount,
  available,
  feePercent,
  amountDecimals = 4,
}: FitMaxAmountWithPercentFeeParams) => {
  const normalizedAmount = normalizeAmount(amount, '')
  const normalizedAvailable = normalizeAmount(available, '')
  const normalizedPercent = normalizeAmount(feePercent, '')

  if (!isValidAmount(normalizedAmount) || !isValidAmount(normalizedAvailable)) {
    return normalizedAmount || '0'
  }

  if (!isValidAmount(normalizedPercent) || Big(normalizedPercent).eq(0)) {
    return stripTrailingZeros(Big(normalizedAmount).toFixed(amountDecimals, 0)) || '0'
  }

  const step = Big(10).pow(-amountDecimals)
  let next = Big(normalizedAmount).toFixed(amountDecimals, 0)

  for (let i = 0; i < 50; i++) {
    if (Big(next).lte(0)) {
      return '0'
    }

    const fee = calculatePercentFeeCeil(next, normalizedPercent, amountDecimals)

    // Строго < available: равенство часто отваливается на бэке из‑за другого округления fee.
    if (Big(next).plus(fee).lt(normalizedAvailable)) {
      return stripTrailingZeros(next) || '0'
    }

    next = Big(next).minus(step).toFixed(amountDecimals, 0)
  }

  return stripTrailingZeros(next) || '0'
}

export const isDecimalPartLongerThanDecimals = (balance: string, decimals: number): boolean => {
  const parts = trim(balance).replace(',', '.').split('.')

  if (!parts[1]) {
    return true
  }

  return parts[1].length <= decimals
}

// Добавляет пробелы как разделители тысяч
export function formatWithThousandSeparators(value: string | number): string {
  const str = String(value).replace(/\s/g, '')

  if (!str) return ''
  const isNegative = str.startsWith('-')
  const unsigned = isNegative ? str.slice(1) : str
  const [intPart = '', decPart] = unsigned.split('.')
  const formatted = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')
  const signed = isNegative ? `-${formatted}` : formatted
  return decPart !== undefined ? `${signed}.${decPart}` : signed
}

export const amountFromScale = (value: TxAmountValue | InternalAmount) => {
  if (typeof value === 'undefined') return '0'

  if (value === null) return '0'

  if (typeof value === 'string' || typeof value === 'number') return String(value)

  return Big(value.amount).div(`1e${value.scale}`).toString()
}
